import { useState, useEffect, useCallback } from "react";
import { Package, Lock, Zap, ExternalLink, Search, Loader2, ShoppingCart } from "lucide-react";
import { Input } from "../components/ui/input";
import { useSubscription } from "../context/SubscriptionContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Products500() {
  const { email, isSubscribed } = useSubscription();
  const [searchParams] = useSearchParams();
  const [purchased, setPurchased] = useState(false);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);

  const checkPurchase = useCallback(async () => {
    if (!email) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/purchase/check`, { params: { email, product_type: "products_500" } });
      setPurchased(res.data.purchased);
      if (res.data.purchased) {
        const dataRes = await axios.get(`${API}/data/products-500`, { params: { email } });
        setProducts(dataRes.data.products);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [email]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      let attempts = 0;
      const poll = async () => {
        try {
          const res = await axios.get(`${API}/purchase/status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            setPurchased(true);
            toast.success("Achat confirme ! Acces debloque.");
            const dataRes = await axios.get(`${API}/data/products-500`, { params: { email } });
            setProducts(dataRes.data.products);
            setLoading(false);
            return;
          }
        } catch { /* ignore */ }
        attempts++;
        if (attempts < 8) {
          setTimeout(poll, 2000);
        } else {
          setLoading(false);
          toast.error("Le paiement est en cours de verification. Actualisez la page dans quelques instants.");
        }
      };
      poll();
    } else {
      checkPurchase();
    }
  }, [email, searchParams, checkPurchase]);

  const handleCheckout = async () => {
    if (!email) {
      toast.error("Entrez votre email dans la page Abonnement d'abord.");
      return;
    }
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/purchase/checkout`, {
        origin_url: window.location.origin,
        email,
        product_type: "products_500",
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de paiement");
      setCheckingOut(false);
    }
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#00E676]" size={32} />
      </div>
    );
  }

  // LOCKED STATE
  if (!purchased) {
    return (
      <div className="space-y-6" data-testid="products-500-page">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3">
            <Package className="text-[#00E676]" size={36} />
            500 PRODUITS
          </h1>
          <p className="text-muted-foreground mt-2 uppercase tracking-wider text-xs sm:text-sm">
            Produits rentables prêts à revendre
          </p>
        </div>

        {/* Preview grid - greyed out */}
        <div className="relative">
          <div className="pointer-events-none select-none filter blur-[6px] opacity-30">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="neo-card p-4">
                  <div className="h-4 bg-muted w-3/4 mb-2" />
                  <div className="h-3 bg-muted w-1/2 mb-3" />
                  <div className="h-5 bg-[#00E676]/20 w-1/3" />
                </div>
              ))}
            </div>
          </div>

          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="neo-card p-8 sm:p-10 max-w-md text-center border-[#00E676] bg-white dark:bg-[#0A0A0A]" data-testid="products-paywall">
              <div className="mx-auto w-20 h-20 bg-[#0A0A0A] dark:bg-[#00E676] border-4 border-[#0A0A0A] flex items-center justify-center mb-6">
                <Lock size={36} className="text-[#00E676] dark:text-[#0A0A0A]" />
              </div>
              <h2 className="text-2xl font-extrabold mb-2">500 Produits Rentables</h2>
              <p className="text-muted-foreground mb-4 text-sm">
                Nouvelle liste chaque mois — 500 produits sourcés avec nom, prix d'achat et lien direct.
              </p>
              <div className="flex items-baseline justify-center gap-1 mb-6">
                <span className="text-4xl font-extrabold">9</span>
                <span className="text-xl font-bold">€</span>
              </div>
              <button
                onClick={handleCheckout}
                disabled={checkingOut}
                className="neo-button-primary w-full flex items-center justify-center gap-2 text-lg py-3"
                data-testid="products-buy-btn"
              >
                {checkingOut ? <Loader2 size={20} className="animate-spin" /> : <Zap size={20} />}
                {checkingOut ? "Redirection..." : "Débloquer — 9€"}
              </button>
              <p className="text-xs text-muted-foreground mt-3">Paiement unique · Nouvelle liste chaque mois</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // UNLOCKED STATE
  return (
    <div className="space-y-6" data-testid="products-500-page">
      <div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3">
          <Package className="text-[#00E676]" size={36} />
          500 PRODUITS
        </h1>
        <p className="text-muted-foreground mt-2 uppercase tracking-wider text-xs sm:text-sm">
          {products.length} produits rentables prêts à revendre
        </p>
      </div>

      <div className="neo-card p-4 sm:p-6">
        <div className="relative">
          <Input
            type="text"
            placeholder="Rechercher un produit..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="neo-input w-full pr-12"
            data-testid="products-search"
          />
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {filtered.length} produit(s) affiché(s)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="products-grid">
        {filtered.map((product, idx) => (
          <div key={idx} className="neo-card p-4 hover:border-[#00E676] transition-colors group" data-testid={`product-card-${idx}`}>
            <div className="flex gap-3 items-start">
              {product.image && (
                <img src={product.image} alt="" className="w-14 h-14 object-contain border border-[#0A0A0A]/20 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm line-clamp-2 group-hover:text-[#00E676] transition-colors">{product.name}</p>
                <p className="text-xl font-extrabold text-[#00E676] mt-1">{product.price.toFixed(2)}€</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[#0A0A0A]/10 dark:border-white/10">
              <a
                href={product.url}
                target="_blank"
                rel="noopener noreferrer"
                className="neo-button-secondary text-xs w-full flex items-center justify-center gap-1.5"
              >
                <ShoppingCart size={14} /> Voir le produit <ExternalLink size={12} />
              </a>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="neo-card p-8 text-center">
          <Search className="mx-auto mb-3 text-muted-foreground" size={40} />
          <p className="font-bold text-muted-foreground">Aucun produit trouvé</p>
        </div>
      )}
    </div>
  );
}
